import DOMPurify from 'dompurify';
import { marked } from 'marked';

function isWhitespaceTextNode(node: ChildNode): boolean {
  return node.nodeType === Node.TEXT_NODE && !(node.textContent || '').trim();
}

function isSpacerParagraph(node: ChildNode): boolean {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const element = node as Element;
  if (element.tagName !== 'P') {
    return false;
  }

  // a paragraph that still holds elements (e.g. a lone <img>) is content, not a spacer
  if (Array.from(element.children).some((child) => child.tagName !== 'BR')) {
    return false;
  }

  const text = (element.textContent || '').replace(/\u00a0/g, '').trim();
  return text.length === 0;
}

function isProseParagraph(node: ChildNode): boolean {
  return (
    node.nodeType === Node.ELEMENT_NODE &&
    (node as Element).tagName === 'P' &&
    !isSpacerParagraph(node)
  );
}

function createSpacerParagraph(doc: Document): HTMLParagraphElement {
  const spacer = doc.createElement('p');
  spacer.innerHTML = '&nbsp;';
  return spacer;
}

/**
 * TipTap paragraphs use margin:0, so consecutive prose paragraphs render flush.
 * Agents space emails with empty/nbsp paragraphs between paragraphs — mirror
 * that after Markdown → HTML so AI drafts keep visible paragraph spacing.
 *
 * Only insert spacers between two prose paragraphs. Blocks like lists keep the
 * surrounding paragraphs tight (a section header stays directly above its
 * bullets) so structured content such as summaries and escalations reads like a
 * normal typed message instead of being double-spaced above and below bullets.
 */
function insertTipTapParagraphSpacers(html: string): string {
  if (!html) {
    return html;
  }

  // parse in an inert document so markup can't load resources or fire
  // handlers while blocks are rearranged
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const container = doc.body;

  const blocks = Array.from(container.childNodes).filter(
    (node) => !isWhitespaceTextNode(node),
  );
  if (blocks.length < 2) {
    return html;
  }

  const fragment = doc.createDocumentFragment();

  blocks.forEach((block, index) => {
    fragment.appendChild(block);

    if (index >= blocks.length - 1) {
      return;
    }

    const next = blocks[index + 1];
    if (isProseParagraph(block) && isProseParagraph(next)) {
      fragment.appendChild(createSpacerParagraph(doc));
    }
  });

  container.replaceChildren(fragment);
  return container.innerHTML;
}

export async function parseAiMarkdownToTipTapHtml(markdown: string): Promise<string> {
  const html = await marked.parse(markdown);
  // marked passes raw HTML in the source through verbatim; strip scripts and
  // event handlers before this reaches the editor, clipboard, or text extraction
  return insertTipTapParagraphSpacers(DOMPurify.sanitize(html));
}
